import {AggregationType, Kpi, KpiOption, KpiResponse} from '@hconnect/common/types'
import {customThemeConstants, dataTestId} from '@hconnect/uikit'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {Check, History} from '@mui/icons-material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import {Box, Button, Stack} from '@mui/material'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Unstable_Grid2'
import {useMemo, useState, FC, useEffect, useCallback} from 'react'
import {FormProvider, useForm, useWatch} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {useKpiNames, useUnits, useCreateKpi} from '../hooks'
import {useKpiNodeIds} from '../hooks/useKpiNodeIds'
import {mapUnitsToOptions, mapKpiOptions} from '../mappers'
import {
  FunctionDefinition,
  KpiAggregationTypeOption,
  KpiFormField,
  KpiUnitOption,
  NewKpi,
  Variable
} from '../types'
import {
  generateDefaultFunction,
  getVisibilityOptions,
  mapAggregationTypesToOptions,
  weightKpiValidator
} from '../utils'

import {FormulaFormFields} from './kpi/FormulaFormFields'
import {KpiFormFields} from './kpi/KpiFormFields'
import {VariableCard} from './kpi/VariableCard'

import {requiredValidator} from '@settings/modules/common/utils'
import {useUrlParam} from '@settings/modules/routing'

type KpiCreateProps = {
  handleClose: () => void
  sensors: Variable[]
  functionsVariables: Variable[]
  onCreateSuccess: (kpi: Kpi) => void
  functionDefinitions?: FunctionDefinition[]
  kpis?: KpiResponse
}

export const KpiCreate: FC<KpiCreateProps> = ({
  kpis,
  sensors,
  functionsVariables,
  functionDefinitions,
  handleClose,
  onCreateSuccess
}) => {
  const {t} = useTranslation()
  const plantCode = useUrlParam('plantCode')

  const isAggregationTypeOption = useCommonFeatureFlag(FeatureFlagKey.KpiAggregationTypeOption, {
    plantId: plantCode
  })
  const [units, setUnits] = useState<KpiUnitOption[]>([])
  const [names, setNames] = useState<KpiOption[]>([])
  const [aggregationTypes] = useState<KpiAggregationTypeOption[]>(
    mapAggregationTypesToOptions(
      Object.values(AggregationType),
      t,
      isAggregationTypeOption ?? false
    )
  )
  const [insertFunctionHandler, setInsertFunctionHandler] = useState<
    ((fn: string) => void) | undefined
  >()
  const unitsResponse = useUnits()
  const {data: kpiNames = []} = useKpiNames((names) => mapKpiOptions(names))
  const {departmentOptions, areaOptions} = useKpiNodeIds()

  const combinedNodeOptions = useMemo(
    () => [...departmentOptions, ...areaOptions],
    [departmentOptions, areaOptions]
  )

  useEffect(() => {
    setUnits(mapUnitsToOptions(unitsResponse.data ?? []))
    setNames(kpiNames)
  }, [kpiNames, unitsResponse.data])

  const visibilityOptions = useMemo(() => getVisibilityOptions(t), [t])
  const weightKpiOptions = (kpis?.processStages[0].definitionsWithResults ?? []).map((item) => ({
    id: String(item.definition.kpiAssignment.id),
    label: item.definition.tagname
  }))

  const unitsList: string[] = unitsResponse.data || []

  const methods = useForm<NewKpi>({
    shouldFocusError: false,
    defaultValues: {
      kpiMainEquipment: departmentOptions.length === 1 ? departmentOptions[0] : null,
      kpiType: null,
      customFormula: '',
      unit: null,
      aggregationType: null,
      weightKpi: null
    }
  })
  const {handleSubmit, control, reset, setValue} = methods
  const aggregationType = useWatch({control, name: 'aggregationType'})

  const config = useMemo(
    () =>
      [
        {name: KpiFormField.KpiMainEquipment, rules: requiredValidator(t)},
        {name: KpiFormField.KpiType, rules: requiredValidator(t)},
        {name: KpiFormField.Unit, rules: requiredValidator(t)},
        {name: KpiFormField.AggregationType, rules: requiredValidator(t)},
        {
          name: KpiFormField.WeightKpi,
          rules: {validate: weightKpiValidator(t, aggregationType)}
        }
      ] as const,
    [t, aggregationType]
  )
  const formulaConfig = useMemo(
    () => ({name: 'customFormula', rules: requiredValidator(t)}) as const,
    [t]
  )

  const {mutate} = useCreateKpi(onCreateSuccess)

  const submit = handleSubmit((data) => {
    if (!data.kpiMainEquipment || !data.kpiType || !data.unit || !data.aggregationType) return
    const {label, id} = data.kpiMainEquipment
    const suffix = ` - ${id}`
    const equipmentName = label.endsWith(suffix) ? label.slice(0, -suffix.length) : label
    const kpiTagName = `${equipmentName} - ${data.kpiType.label}`
    mutate({
      plantCode,
      kpiData: {
        departmentUpmId: id,
        formula: data.customFormula,
        kpiTagName,
        kpiType: data.kpiType.label,
        unit: data.unit.id,
        aggregationType: data.aggregationType.id,
        weightKpiAssignmentId: data.weightKpi?.id
      }
    })
  })

  const insertDefaultFunction = useCallback(
    (functionName: string) => {
      const functionDefinition = functionDefinitions?.find(({name}) => name === functionName)
      if (functionDefinition) insertFunctionHandler?.(generateDefaultFunction(functionDefinition))
    },
    [functionDefinitions, insertFunctionHandler]
  )

  const handleAddCustomUnit = (customUnit: string) => {
    if (!customUnit) {
      setUnits(mapUnitsToOptions(unitsList))
      return
    }
    const newUnit = {id: customUnit.toLowerCase(), label: customUnit}
    setUnits([...mapUnitsToOptions(unitsList), newUnit])
  }
  const handleAddCustomName = (customName: string) => {
    if (!customName) {
      setNames(kpiNames)
      return
    }
    const newName = {id: customName.toLowerCase(), label: customName}
    setNames([...kpiNames, newName])
  }

  const handleUnitChange = () => {
    if (unitsResponse.data && control._formValues.unit?.label?.includes('/')) {
      const weightAverageOption = aggregationTypes.find(
        (option) => option.id === AggregationType.WeightAverage
      )
      if (weightAverageOption) {
        setValue('aggregationType', weightAverageOption, {
          shouldDirty: true,
          shouldTouch: true
        })
      }
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={submit}>
        <Grid container spacing={2}>
          <Grid xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Stack>
                <Button
                  onClick={handleClose}
                  startIcon={<ChevronLeftIcon />}
                  variant="text"
                  sx={{p: 0.5, pl: 0, justifyContent: 'flex-start', display: 'flex'}}
                  size="small"
                  data-test-id="create-kpi-close-button"
                >
                  {t('kpiCalculationSettings.action.backToList')}
                </Button>
                <Typography variant="h4">{t('kpiCalculationSettings.label.kpiDetails')}</Typography>
              </Stack>
              <Box display="flex" gap={1}>
                <Button
                  color="secondary"
                  startIcon={<History />}
                  onClick={() => reset()}
                  data-test-id="create-kpi-reset-button"
                >
                  {t('kpiCalculationSettings.action.revert')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Check />}
                  type="submit"
                  data-test-id="create-kpi-save-button"
                >
                  {t('kpiCalculationSettings.action.save')}
                </Button>
              </Box>
            </Box>
            <KpiFormFields
              control={control}
              config={config}
              handleAddCustomUnit={handleAddCustomUnit}
              visibilityOptions={visibilityOptions}
              units={units}
              equipmentOptions={combinedNodeOptions}
              weightKpiOptions={weightKpiOptions}
              aggregationTypes={aggregationTypes}
              aggregationType={aggregationType}
              handleUnitChange={handleUnitChange}
              kpiNames={names}
              handleAddCustomName={handleAddCustomName}
            />
          </Grid>
          <Grid xs={12} mb={2}>
            <Typography variant="h4" my={2}>
              {t('kpiCalculationSettings.label.formula')}
            </Typography>
            <FormulaFormFields
              functions={functionDefinitions}
              config={formulaConfig}
              control={control}
              onRegisterInsertHandler={(handler) => setInsertFunctionHandler(() => handler)}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <VariableCard
              variables={sensors}
              label={t('kpiCalculationSettings.label.insertSensor')}
              nameSx={{color: 'primary.light'}}
              {...dataTestId('sensor_card')}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <VariableCard
              variables={functionsVariables}
              label={t('kpiCalculationSettings.label.insertFunction')}
              onClick={insertDefaultFunction}
              nameSx={{color: customThemeConstants().palette.lightChart1}}
              {...dataTestId('function_card')}
            />
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  )
}
